import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

// THUNKS
// Ottiene la lista di tutti i veicoli interni
export const getInternalVehiclesList = createAsyncThunk(
    'internalVehicle/getInternalVehiclesList',
    async ({ accountId, token, search, start, length, order }, { rejectWithValue }) => {
    try {
        let url = `${import.meta.env.VITE_API_BASE_URL}/vehicles/list?accountId=${accountId}&start=${start}&length=${length}&draw=1&internal=true`;
        
        // Aggiungo i parametri di ricerca se presenti
        if (search) {
            url += `&search[value]=${encodeURIComponent(search)}`;
        }
        
        // Aggiungo il parametro di ordinamento se presente
        if (order) {
            url += `&order=${encodeURIComponent(JSON.stringify(order))}`;
        }
        
        const headers = {
            'Content-Type': 'application/json',
            'auth-token': token
        };
        const options = { method: 'GET', headers };
        
        const response = await fetch(url, options);
        
        if (!response.ok) {
            const error = await response.json();
            return rejectWithValue({
                message: error.message || `Errore sconosciuto nel caricamento dei veicoli interni.`
            });
        }
        const result = await response.json();
        return result;
    } catch (error) {
        return rejectWithValue({
            message: (error instanceof Error) ? error.message : 'Errore di rete. Riprova più tardi.'
        });
    }
});

// Ottiene i dettagli di un veicolo specifico
export const getInternalVehicleDetails = createAsyncThunk(
    'internalVehicle/getInternalVehicleDetails',
    async ({ vehicleId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/vehicles/details?vehicleId=${vehicleId}`;
        const headers = {
            'Content-Type': 'application/json',
            'auth-token': token
        };
        const options = { method: 'GET', headers };
        
        const response = await fetch(url, options);
        
        if (!response.ok) {
            const error = await response.json();
            return rejectWithValue({
                message: error.message || `Errore sconosciuto nel caricamento dei dettagli del veicolo.`
            });
        }
        const result = await response.json();
        return result;
    } catch (error) {
        return rejectWithValue({
            message: (error instanceof Error) ? error.message : 'Errore di rete. Riprova più tardi.'
        });
    }
});

// Crea un nuovo veicolo interno
export const createInternalVehicle = createAsyncThunk(
    'internalVehicle/createInternalVehicle',
    async ({ vehicle, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/vehicles`;
        const headers = {
            'Content-Type': 'application/json',
            'auth-token': token
        };
        const options = { 
            method: 'POST', 
            headers, 
            body: JSON.stringify({ vehicle })
        };
        
        const response = await fetch(url, options);
        
        if (!response.ok) {
            const error = await response.json();
            return rejectWithValue({
                message: error.message || `Errore sconosciuto nella creazione del veicolo interno.`
            });
        }
        const result = await response.json();
        return result;
    } catch (error) {
        return rejectWithValue({
            message: (error instanceof Error) ? error.message : 'Errore di rete. Riprova più tardi.'
        });
    }
});

// Aggiorna un veicolo esistente
export const updateInternalVehicle = createAsyncThunk(
    'internalVehicle/updateInternalVehicle',
    async ({ vehicle, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/vehicles`;
        const headers = {
            'Content-Type': 'application/json',
            'auth-token': token
        };
        const options = { 
            method: 'PATCH', 
            headers, 
            body: JSON.stringify({ vehicle })
        };
        
        const response = await fetch(url, options);
        
        if (!response.ok) {
            const error = await response.json();
            return rejectWithValue({
                message: error.message || `Errore sconosciuto nell'aggiornamento del veicolo.`
            });
        }
        const result = await response.json();
        return result;
    } catch (error) {
        return rejectWithValue({
            message: (error instanceof Error) ? error.message : 'Errore di rete. Riprova più tardi.'
        });
    }
});

// Elimina un veicolo
export const deleteInternalVehicle = createAsyncThunk(
    'internalVehicle/deleteInternalVehicle',
    async ({ vehicleId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/vehicles`;
        const headers = {
            'Content-Type': 'application/json',
            'auth-token': token
        };
        const options = { 
            method: 'DELETE', 
            headers, 
            body: JSON.stringify({ vehicleId })
        };
        
        const response = await fetch(url, options);
        
        if (!response.ok) {
            const error = await response.json();
            return rejectWithValue({
                message: error.message || `Errore sconosciuto nell'eliminazione del veicolo.`
            });
        }
        const result = await response.json();
        return result;
    } catch (error) {
        return rejectWithValue({
            message: (error instanceof Error) ? error.message : 'Errore di rete. Riprova più tardi.'
        });
    }
});

const initialState = {
    data: null,
    internalVehicleDetails: null,
    error: null,
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false
};

const internalVehicleSlice = createSlice({
    name: 'internalVehicle',
    initialState,
    reducers: {
        clearInternalVehicleData: (state) => {
            state.data = null;
            state.internalVehicleDetails = null;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // getInternalVehiclesList
            .addCase(getInternalVehiclesList.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getInternalVehiclesList.fulfilled, (state, action) => {
                state.data = action.payload;
                state.error = null;
                state.isLoading = false;
            })
            .addCase(getInternalVehiclesList.rejected, (state, action) => {
                state.error = action.payload?.message || "Errore sconosciuto nel caricamento dei veicoli interni.";
                state.isLoading = false;
            })
            // getInternalVehicleDetails
            .addCase(getInternalVehicleDetails.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getInternalVehicleDetails.fulfilled, (state, action) => {
                state.internalVehicleDetails = action.payload;
                state.error = null;
                state.isLoading = false;
            })
            .addCase(getInternalVehicleDetails.rejected, (state, action) => {
                state.error = action.payload?.message || "Errore sconosciuto nel caricamento dei dettagli del veicolo.";
                state.isLoading = false;
            })
            // createInternalVehicle
            .addCase(createInternalVehicle.pending, (state) => {
                state.isCreating = true;
                state.error = null;
            })
            .addCase(createInternalVehicle.fulfilled, (state, action) => {
                state.internalVehicleDetails = action.payload;
                state.error = null;
                state.isCreating = false;
            })
            .addCase(createInternalVehicle.rejected, (state, action) => {
                state.error = action.payload?.message || "Errore sconosciuto nella creazione del veicolo interno.";
                state.isCreating = false;
            })
            // updateInternalVehicle
            .addCase(updateInternalVehicle.pending, (state) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(updateInternalVehicle.fulfilled, (state, action) => {
                state.internalVehicleDetails = action.payload;
                state.error = null;
                state.isUpdating = false;
            })
            .addCase(updateInternalVehicle.rejected, (state, action) => {
                state.error = action.payload?.message || "Errore sconosciuto nell'aggiornamento del veicolo.";
                state.isUpdating = false;
            })
            // deleteInternalVehicle
            .addCase(deleteInternalVehicle.pending, (state) => {
                state.isDeleting = true;
                state.error = null;
            })
            .addCase(deleteInternalVehicle.fulfilled, (state) => {
                state.internalVehicleDetails = null;
                state.error = null;
                state.isDeleting = false;
            })
            .addCase(deleteInternalVehicle.rejected, (state, action) => {
                state.error = action.payload?.message || "Errore sconosciuto nell'eliminazione del veicolo.";
                state.isDeleting = false;
            })
    }
});

// Selectors
export const selectInternalVehiclesData = (state) => state.internalVehicle.data;
export const selectInternalVehicleDetails = (state) => state.internalVehicle.internalVehicleDetails;
export const selectInternalVehicleLoading = (state) => state.internalVehicle.isLoading;
export const selectInternalVehicleCreating = (state) => state.internalVehicle.isCreating;
export const selectInternalVehicleUpdating = (state) => state.internalVehicle.isUpdating;
export const selectInternalVehicleDeleting = (state) => state.internalVehicle.isDeleting;
export const selectInternalVehicleError = (state) => state.internalVehicle.error;

export const { clearInternalVehicleData } = internalVehicleSlice.actions;
export default internalVehicleSlice.reducer;
